//
//  capacitor_compat.js
//  Cell-Ed
//
//  Platform detection that works across every Capacitor runtime we ship against.
//
import { Capacitor } from '@capacitor/core';

/**
 * Capacitor 3 replaced the `Capacitor.platform` and `Capacitor.isNative` properties with
 * `getPlatform()` and `isNativePlatform()`. Capacitor 6 still injected the old properties, so
 * reading them directly worked; Capacitor 8 removed them, and reading `.platform` there yields
 * `undefined` — which throws on `.startsWith(...)` and silently inverts `if (isNative)` guards.
 *
 * The runtimes this web app actually runs inside today:
 *
 *   Android (Capacitor 8, in progress)  getPlatform ✓   platform ✗   isNativePlatform ✓   isNative ✗
 *   Android (Capacitor 6, shipping)     getPlatform ✓   platform ✓   isNativePlatform ✓   isNative ✓
 *   iOS app (Capacitor 2-era runtime)   getPlatform ✓   platform ✓   isNativePlatform ✗   isNative ✓
 *   Browser (bundled @capacitor/core 2) getPlatform ✓   platform ✓   isNativePlatform ✗   isNative ✓
 *
 * Both fallbacks are load-bearing, not defensive padding:
 *   - `isNativePlatform` is absent on iOS and in the browser, so `capIsNative()` must fall
 *     back to `.isNative` or it returns undefined there and disables native-only features.
 *   - `.platform` is absent on Capacitor 8, so `capPlatform()` must prefer `getPlatform()`.
 *
 * Do not "simplify" either of these to a single call.
 */

/**
 * The platform this app is running on: 'android', 'ios' or 'web'.
 * @return {string}
 */
export const capPlatform = () =>
  (Capacitor.getPlatform ? Capacitor.getPlatform() : Capacitor.platform) || 'web';

/**
 * Whether we are running inside a native shell rather than a browser.
 * @return {boolean}
 */
export const capIsNative = () =>
  Capacitor.isNativePlatform ? Capacitor.isNativePlatform() : !!Capacitor.isNative;

/** Convenience helpers, so call sites read as intent rather than string comparison. */
export const capIsAndroid = () => capPlatform().startsWith('android');
export const capIsIOS = () => capPlatform().startsWith('ios');
